import { NavLink, Text, Title } from '@mantine/core';
import { useAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useRef, type MouseEvent } from 'react';

import { useCatalogLabels } from '../labels';
import { expandedTreeNodesAtom } from '../state';
import { nodeLabel, type TreeNode } from '../types';

interface NavigationTreeProps {
    nodes: TreeNode[];
    counts: Record<string, number>;
    selectedNodeId?: string;
    onSelect: (nodeId?: string) => void;
    title: string;
    locale: string;
    /** Suppress the internal heading - for the mobile nav Drawer, whose own `title` prop already
     * shows it (avoids a duplicate "Kategorien" heading). Defaults to true (desktop sidebar use). */
    showTitle?: boolean;
}

interface TreeRowProps {
    node: TreeNode;
    counts: Record<string, number>;
    selectedNodeId?: string;
    onSelect: (nodeId?: string) => void;
    locale: string;
    /** Open branches - see state.expandedTreeNodesAtom. */
    expanded: ReadonlySet<string>;
    onToggle: (nodeId: string) => void;
    /** 0 for the top level, which is set in a heavier weight. */
    depth: number;
}

/** Width of the expand/collapse column. Every row reserves it - branches for their chevron, leaves
 * for nothing - so all labels in the tree start on ONE vertical line instead of stepping in and out
 * depending on whether a node happens to have children. */
const CHEVRON_WIDTH = 18;

/** Width of the count column. Wide enough for the largest count in the catalog today (575) with
 * room to spare; the counts are right-aligned inside it and set in tabular figures, so they form a
 * straight column at every indentation level. */
const COUNT_WIDTH = 38;

/** Node ids from the top level down to `nodeId` (inclusive), or [] if it is not in `nodes`. */
const pathTo = (nodes: TreeNode[], nodeId: string): string[] => {
    for (const node of nodes) {
        if (node.nodeId === nodeId) {
            return [node.nodeId];
        }
        const below = pathTo(node.children, nodeId);
        if (below.length > 0) {
            return [node.nodeId, ...below];
        }
    }
    return [];
};

/** True if `nodeId` is `node` itself or lies somewhere in its subtree. */
const containsNodeId = (node: TreeNode, nodeId?: string): boolean => {
    if (!nodeId) {
        return false;
    }
    if (node.nodeId === nodeId) {
        return true;
    }
    return node.children.some((child) => containsNodeId(child, nodeId));
};

/** A branch is shown when it has a count, or when the current selection sits inside it - counts
 * follow the active filters/search, so the selected node can drop to 0 and hiding it there would
 * strand the user on a selection they can no longer see or step out of. */
const isVisible = (node: TreeNode, counts: Record<string, number>, selectedNodeId?: string): boolean =>
    (counts[node.nodeId] ?? 0) > 0 || containsNodeId(node, selectedNodeId);

/**
 * The expand/collapse indicator, in a fixed-width leading column.
 *
 * It replaces Mantine's own automatic chevron, which NavLink renders on the RIGHT for any node with
 * children. That default put two different things in the same place - a chevron on branches, a
 * count on leaves - and left the counts at a different x per row (they followed the label, and the
 * chevron pushed them further along again). Leading the row with the indicator and trailing it with
 * the count gives each its own column.
 */
interface ExpandMarkerProps {
    hasChildren: boolean;
    opened: boolean;
    /** Opens/closes the branch WITHOUT selecting it - the chevron is its own target. */
    onToggle?: () => void;
}

const ExpandMarker = ({ hasChildren, opened, onToggle }: ExpandMarkerProps) => (
    <span
        className="pih-tree-chevron flex items-center justify-center"
        style={{ width: CHEVRON_WIDTH, flex: 'none', cursor: hasChildren ? 'pointer' : undefined }}
        aria-hidden
        onClick={
            hasChildren && onToggle
                ? (event: MouseEvent) => {
                      // The row itself selects the node - the chevron only folds.
                      event.preventDefault();
                      event.stopPropagation();
                      onToggle();
                  }
                : undefined
        }
    >
        {hasChildren && (
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                {opened ? 'expand_more' : 'chevron_right'}
            </span>
        )}
    </span>
);

/**
 * One node and - only while it is open - its children.
 *
 * Memoized, and the children are rendered only for an OPEN branch: with the full STIBO delivery
 * the tree had 143 visible rows, each a Mantine NavLink + Collapse + Box, and Collapse keeps closed
 * children mounted. Every render of the catalog page re-rendered all of them - opening an article
 * cost ~500 ms of the ~600 ms click (measured in a DevTools trace, React dev build). Now an
 * unchanged row is skipped, and a closed branch costs one row. A closed branch has no expand
 * animation to keep: it is controlled by `expanded` (the user's open branches), not by Mantine's
 * own toggle.
 */
const TreeRow = memo(({ node, counts, selectedNodeId, onSelect, locale, expanded, onToggle, depth }: TreeRowProps) => {
    const count = counts[node.nodeId] ?? 0;
    if (!isVisible(node, counts, selectedNodeId)) {
        // Hide branches with nothing to show (e.g. ones that only contain parts - parts never
        // appear in navigation - or ones the active filters emptied out).
        return null;
    }
    const children = node.children.filter((child) => isVisible(child, counts, selectedNodeId));
    const opened = children.length > 0 && expanded.has(node.nodeId);
    const active = selectedNodeId === node.nodeId;
    return (
        <NavLink
            active={active}
            aria-expanded={children.length > 0 ? opened : undefined}
            childrenOffset={CHEVRON_WIDTH}
            className="pih-tree-link"
            classNames={{ children: 'pih-tree-children' }}
            // A closed branch that holds the selection says so (bold), so the way back to the
            // selected category is visible even with its branch folded away.
            data-holds-active={!active && !opened && containsNodeId(node, selectedNodeId) ? true : undefined}
            data-top-level={depth === 0 ? true : undefined}
            // Mantine rotates `rightSection` whenever a NavLink with children is opened - the
            // default assumes that section holds the expand chevron, which is supposed to turn.
            // Here it holds the COUNT, and an opened branch rendered its number on its side
            // (reported: "In der Navigation wird beim Klick die Anzahl gedreht" - only ever on the
            // opened rows, never on the collapsed ones). The chevron moved to `leftSection`, so
            // there is nothing left here that should rotate.
            label={nodeLabel(node, locale)}
            leftSection={
                <ExpandMarker
                    hasChildren={children.length > 0}
                    opened={opened}
                    onToggle={() => onToggle(node.nodeId)}
                />
            }
            opened={opened}
            rightSection={
                <Text
                    c="dimmed"
                    size="xs"
                    style={{ width: COUNT_WIDTH, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                >
                    {count}
                </Text>
            }
            // The full name on hover - a narrow sidebar cuts long ones ("Aluminiumschalu…").
            title={nodeLabel(node, locale)}
            disableRightSectionRotation
            // A click selects the node (and opens it, see NavigationTree); a second click on the
            // already selected node folds it again.
            onClick={() => (active && children.length > 0 ? onToggle(node.nodeId) : onSelect(node.nodeId))}
        >
            {opened &&
                children.map((child) => (
                    <TreeRow
                        key={child.nodeId}
                        counts={counts}
                        depth={depth + 1}
                        expanded={expanded}
                        locale={locale}
                        node={child}
                        selectedNodeId={selectedNodeId}
                        onSelect={onSelect}
                        onToggle={onToggle}
                    />
                ))}
        </NavLink>
    );
});
TreeRow.displayName = 'TreeRow';

export const NavigationTree = ({
    nodes,
    counts,
    selectedNodeId,
    onSelect,
    title,
    locale,
    showTitle = true,
}: NavigationTreeProps) => {
    const { L } = useCatalogLabels();

    // The page passes a new handler on every render; the rows get one stable function that always
    // calls the latest - otherwise the memoized rows above would re-render every time anyway.
    const onSelectRef = useRef(onSelect);
    useEffect(() => {
        onSelectRef.current = onSelect;
    }, [onSelect]);
    const select = useCallback((nodeId?: string) => onSelectRef.current(nodeId), []);

    // Open branches are the user's: a click on one node no longer closes the others (reported:
    // "Können wir geöffnete Knoten offen lassen?"). Before, "open" simply meant "on the path to the
    // selection", so every click folded away whatever else was open. Now a branch only closes when
    // its own chevron (or the selected node itself) is clicked; selecting a node - here, from the
    // breadcrumb or from a shared link - opens the path down to it in addition.
    const [expandedIds, setExpandedIds] = useAtom(expandedTreeNodesAtom);
    useEffect(() => {
        if (!selectedNodeId) {
            return;
        }
        const path = pathTo(nodes, selectedNodeId);
        setExpandedIds((prev) => (path.every((id) => prev.includes(id)) ? prev : [...new Set([...prev, ...path])]));
    }, [nodes, selectedNodeId, setExpandedIds]);
    const toggle = useCallback(
        (nodeId: string) =>
            setExpandedIds((prev) => (prev.includes(nodeId) ? prev.filter((id) => id !== nodeId) : [...prev, nodeId])),
        [setExpandedIds]
    );
    const expanded = useMemo(() => new Set(expandedIds), [expandedIds]);

    return (
        <div>
            {showTitle && (
                <Title mb="xs" order={5}>
                    {title}
                </Title>
            )}
            <NavLink
                active={!selectedNodeId}
                className="pih-tree-link"
                label={L.allProducts}
                // Same empty leading column as every other row, so "Alle Produkte" starts on the
                // tree's single label line rather than 18px to its left.
                leftSection={<ExpandMarker hasChildren={false} opened={false} />}
                onClick={() => select(undefined)}
            />
            {nodes
                .filter((node) => isVisible(node, counts, selectedNodeId))
                .map((node) => (
                    <TreeRow
                        key={node.nodeId}
                        counts={counts}
                        depth={0}
                        expanded={expanded}
                        locale={locale}
                        node={node}
                        selectedNodeId={selectedNodeId}
                        onSelect={select}
                        onToggle={toggle}
                    />
                ))}
        </div>
    );
};
